import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import productBigBags from '../assets/img/products/bigbag.jpg';
import productPeFilm from '../assets/img/products/pefilm.jpg';
import productBags from '../assets/img/products/sack.jpg';

// Hero carousel images
import heroBigBag from '../assets/img/products/bigbag/bigbag.jpg';
import heroBigBag0 from '../assets/img/products/bigbag/bigbag0.jpg';
import heroBigBag1 from '../assets/img/products/bigbag/bigbag1.jpg';

import './Home.css';

const heroImages = [
  heroBigBag,
  heroBigBag0,
  heroBigBag1
];

const industriesData = [
  { name: 'Agriculture', icon: 'fas fa-tractor' },
  { name: 'Chemical', icon: 'fas fa-flask' },
  { name: 'Construction', icon: 'fas fa-hard-hat' },
  { name: 'Food', icon: 'fas fa-utensils' },
  { name: 'Mining', icon: 'fas fa-gem' },
  { name: 'Pharmaceutical', icon: 'fas fa-pills' },
  { name: 'Recycling', icon: 'fas fa-recycle' },
  { name: 'Logistics', icon: 'fas fa-truck-loading' }
];

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const nextImage = () => {
    setCurrentImageIndex(currentImageIndex === heroImages.length - 1 ? 0 : currentImageIndex + 1);
  };

  const prevImage = () => {
    setCurrentImageIndex(currentImageIndex === 0 ? heroImages.length - 1 : currentImageIndex - 1);
  };

  // Helper function to handle navigation with scroll to top
  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  return (
    <>
      {/* Hero Carousel Section */}
      <section id="hero" className="hero-carousel">
        <div className="carousel-container">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`carousel-slide ${index === currentImageIndex ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            >
              <div className="carousel-overlay"></div>
            </div>
          ))}
          
          <div className="carousel-controls">
            <button className="carousel-btn prev" onClick={prevImage}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <button className="carousel-btn next" onClick={nextImage}>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
          
          <div className="carousel-indicators">
            {heroImages.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </div>
        
        <div className="container">
          <div className="row">
            <div className="col-lg-8 hero-content">
              <h1>Industrial Packaging Solutions</h1>
              <h2>Specializing in High-Quality Big Bags & More</h2>
              <div className="hero-buttons">
                <a href="#featured-products" className="btn-get-started scrollto">Our Products</a>
                <button 
                  onClick={() => handleNavigation('/contact')} 
                  className="btn-contact scrollto"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section id="featured-products" className="featured-products">
        <div className="container">
          <div className="section-title">
            <h2>Featured Products</h2>
            <p>Our most popular industrial packaging solutions</p>
          </div>
          <div className="row">
            <div className="col-lg-4 col-md-6 mb-4 d-flex align-items-stretch">
              <div className="product-card">
                <div className="product-img">
                  <img src={productBigBags} alt="Big Bags" className="img-fluid" />
                </div>
                <div className="product-info">
                  <h4>Big Bags</h4>
                  <p>High-quality FIBC bags designed for industrial use</p>
                  <button 
                    onClick={() => handleNavigation('/products/big-bags')} 
                    className="btn-learn-more"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-4 d-flex align-items-stretch">
              <div className="product-card">
                <div className="product-img">
                  <img src={productPeFilm} alt="PE Film" className="img-fluid" />
                </div>
                <div className="product-info">
                  <h4>PE Film</h4>
                  <p>Premium barrier and shrink films for optimal protection</p>
                  <button 
                    onClick={() => handleNavigation('/products/special-products-pe-film')} 
                    className="btn-learn-more"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-4 d-flex align-items-stretch">
              <div className="product-card">
                <div className="product-img">
                  <img src={productBags} alt="Bags" className="img-fluid" />
                </div>
                <div className="product-info">
                  <h4>Industrial PP Bags</h4>
                  <p>Durable bags for various industrial applications</p>
                  <button 
                    onClick={() => handleNavigation('/products/bags')} 
                    className="btn-learn-more"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about section-bg">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title">
                <h2>About Us</h2>
              </div>
              <div className="content">
                <div className="about-highlight">
                  <div className="highlight-box">
                    <i className="fas fa-award"></i>
                    <span>35+ Years of Excellence</span>
                  </div>
                  <div className="highlight-box">
                    <i className="fas fa-globe"></i>
                    <span>Global Reach</span>
                  </div>
                  <div className="highlight-box">
                    <i className="fas fa-certificate"></i>
                    <span>ISO Certified</span>
                  </div>
                </div>
                <p>
                  For over 35 years, we have been a leading manufacturer of industrial packaging solutions.
                  Our dedication to quality, innovation, and customer satisfaction has made us a trusted partner
                  for businesses worldwide.
                </p>
                <p>
                  We specialize in producing a wide range of packaging products including big bags, bags,
                  PE films, twines, and special packaging solutions tailored to meet the specific needs of
                  various industries.
                </p>
                <div className="mt-4 text-center">
                  <button 
                    onClick={() => handleNavigation('/about')} 
                    className="btn-read-more"
                  >
                    Read More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="why-us">
        <div className="container">
          <div className="section-title">
            <h2>Why Choose Us</h2>
            <p>Our commitment to excellence sets us apart</p>
          </div>
          <div className="row">
            <div className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
              <div className="icon-box">
                <div className="icon"><i className="fas fa-box"></i></div>
                <h4>Quality Products</h4>
                <p>Our products undergo rigorous quality control to ensure they meet the highest standards</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
              <div className="icon-box">
                <div className="icon"><i className="fas fa-certificate"></i></div>
                <h4>Certified Solutions</h4>
                <p>All our manufacturing processes and products are certified to international standards</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
              <div className="icon-box">
                <div className="icon"><i className="fas fa-users"></i></div>
                <h4>Expert Team</h4>
                <p>Our experienced team provides professional guidance and support</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
              <div className="icon-box">
                <div className="icon"><i className="fas fa-truck"></i></div>
                <h4>Global Delivery</h4>
                <p>We ensure reliable and efficient delivery to customers worldwide</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
              <div className="icon-box">
                <div className="icon"><i className="fas fa-chart-line"></i></div>
                <h4>Innovation</h4>
                <p>We continuously invest in research and development to offer innovative solutions</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
              <div className="icon-box">
                <div className="icon"><i className="fas fa-leaf"></i></div>
                <h4>Sustainability</h4>
                <p>Our commitment to environmental responsibility guides our operations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section id="industries" className="industries section-bg">
        <div className="container">
          <div className="section-title">
            <h2>Industries We Serve</h2>
            <p>Our packaging solutions cater to diverse industrial needs</p>
          </div>
          <div className="row">
            {industriesData.map((industry, index) => (
              <div key={index} className="col-lg-3 col-md-4 col-6 industry-box d-flex flex-column align-items-center">
                <div className="industry-icon">
                  <i className={industry.icon}></i>
                </div>
                <h4>{industry.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section id="cta" className="cta">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <h3>Need Custom Packaging Solutions?</h3>
              <p>Contact our team today to discuss your specific requirements and learn how we can develop tailored packaging solutions for your business.</p>
              <div className="cta-btn-container mt-3">
                <button 
                  onClick={() => handleNavigation('/contact')} 
                  className="cta-btn"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;